// Renders the device inventory in fixed functional-category sections. Empty
// categories stay hidden and rows use the Devices page's selected sort order.

import { CATEGORIES, deviceCategory } from '../../lib/deviceCategory';
import { sortDevices, type DeviceSortKey } from '../../lib/deviceInventory';
import type { VesselDevice } from '../../lib/useBoatDevices';
import { CategoryIcon } from '../app/icons';
import { DeviceCard } from './DeviceCard';

interface Section {
  key: string;
  label: string;
  icon: React.ReactNode;
  devices: VesselDevice[];
}

function categorySections(devices: VesselDevice[], sortBy: DeviceSortKey): Section[] {
  return CATEGORIES.map((category) => ({
    key: category.key,
    label: category.label,
    icon: <CategoryIcon category={category.key} className="sec-ico" />,
    devices: sortDevices(
      devices.filter((device) => deviceCategory(device) === category.key),
      sortBy
    ),
  })).filter((section) => section.devices.length > 0);
}

export function DeviceSections({
  devices,
  boatId,
  sortBy,
  now = Date.now(),
  twinsByLead,
}: {
  devices: VesselDevice[];
  boatId: string;
  sortBy: DeviceSortKey;
  now?: number;
  /** Lead deviceId → nested protocol twins (lib/deviceGrouping). */
  twinsByLead?: Map<string, VesselDevice[]>;
}) {
  const sections = categorySections(devices, sortBy);

  return (
    <div>
      {sections.map((section) => (
        <section
          className="device-section"
          data-testid={`device-section-${section.key}`}
          key={section.key}
        >
          <div className="section-head">
            {section.icon}
            <h3>{section.label}</h3>
            <span className="sec-count">{section.devices.length}</span>
          </div>
          <ul
            className="section-grid section-grid--list"
            data-testid={`device-list-${section.key}`}
          >
            {section.devices.map((device) => (
              <li key={device.deviceId}>
                <DeviceCard
                  device={device}
                  boatId={boatId}
                  now={now}
                  twins={twinsByLead?.get(device.deviceId)}
                />
              </li>
            ))}
          </ul>
        </section>
      ))}
    </div>
  );
}
